/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import appOrigin from '@sportninja/common/constants/appOrigin'
import { ROUTES } from '@sportninja/common/constants/app'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import dayjs from 'dayjs'
import ShareIcon from '../../components/icons/ShareIcon'
import ShareModal from '../../components/ShareModal'
import Text from 'src/components/ts-components/Text'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'
const VIDEO_PLACEHOLDER = '/images/generic_placeholders/placeholder_video.png'

const PlayCircleIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='32'
    height='32'
    viewBox='0 0 32 32'
    fill='none'
  >
    <path
      d='M14.3998 21.2L20.6265 16.5333C20.9865 16.2666 20.9865 15.7333 20.6265 15.4666L14.3998 10.8C13.9598 10.4666 13.3332 10.7866 13.3332 11.3333V20.6666C13.3332 21.2133 13.9598 21.5333 14.3998 21.2ZM15.9998 2.66663C8.63984 2.66663 2.6665 8.63996 2.6665 16C2.6665 23.36 8.63984 29.3333 15.9998 29.3333C23.3598 29.3333 29.3332 23.36 29.3332 16C29.3332 8.63996 23.3598 2.66663 15.9998 2.66663ZM15.9998 26.6666C10.1198 26.6666 5.33317 21.88 5.33317 16C5.33317 10.12 10.1198 5.33329 15.9998 5.33329C21.8798 5.33329 26.6665 10.12 26.6665 16C26.6665 21.88 21.8798 26.6666 15.9998 26.6666Z'
      fill='white'
    />
  </svg>
)

const cardStyle = css`
  background: ${colors.SECONDARY_400};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

const thumbnailContainerStyle = css`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 8px;
`

const thumbnailStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const contentStyle = css`
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const teamRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const teamAbbrStyle = css`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${colors.NEUTRAL_0};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const teamImgStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: contain;
`

const vsStyle = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${colors.NEUTRAL_50};
`

const dateStyle = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
`

const shareButtonStyle = css`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`

const playingOverlayStyle = css`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
`

const playingThumbnailBorderStyle = css`
  border: 2px solid ${colors.PRIMARY};
  border-radius: 8px;
`

const TeamLink = ({ team }) => {
  if (!team) {
    return null
  }
  return (
    <Link
      to={`${ROUTES.TEAM_ROOT}/${team.id}`}
      css={teamAbbrStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {team.abbreviation || team.name}
    </Link>
  )
}

const TeamLogo = ({ team }) => {
  if (!team) {
    return null
  }
  return (
    <Link
      to={`${ROUTES.TEAM_ROOT}/${team.id}`}
      onClick={(e) => e.stopPropagation()}
      css={css`
        display: flex;
        flex-shrink: 0;
      `}
    >
      <img
        src={getImageThumbnailId(team) || TEAM_PLACEHOLDER}
        alt={team.abbreviation || team.name || ''}
        css={teamImgStyle}
        onError={(e) => {
          e.target.src = TEAM_PLACEHOLDER
        }}
      />
    </Link>
  )
}

const PlayerHighlightCard = ({ highlight, onClick, isPlaying = false }) => {
  const [isShareOpen, setIsShareOpen] = useState(false)
  const homeTeam = highlight.game?.home_team
  const visitingTeam = highlight.game?.visiting_team

  const canShare = !!highlight.player_id
  const shareUrl = `${appOrigin}/highlight/${highlight.id}?playerId=${highlight.player_id}`

  const handleShareClick = (e) => {
    e.stopPropagation()
    setIsShareOpen(true)
  }

  return (
    <div css={cardStyle} onClick={() => onClick(highlight)}>
      <div
        css={[
          thumbnailContainerStyle,
          isPlaying && playingThumbnailBorderStyle,
        ]}
      >
        <img
          src={resolveAssetUrl(highlight.thumbnail_url) || VIDEO_PLACEHOLDER}
          alt={highlight.title}
          css={thumbnailStyle}
          onError={(e) => {
            e.target.src = VIDEO_PLACEHOLDER
          }}
        />
        {isPlaying && (
          <div css={playingOverlayStyle}>
            <PlayCircleIcon />
          </div>
        )}
        {canShare && !isPlaying && (
          <button css={shareButtonStyle} onClick={handleShareClick}>
            <ShareIcon size={14} color={colors.WHITE} />
          </button>
        )}
      </div>
      <div css={contentStyle}>
        {(homeTeam || visitingTeam) && (
          <div css={teamRowStyle}>
            <TeamLink team={homeTeam} />
            <TeamLogo team={homeTeam} />
            <span css={vsStyle}>vs</span>
            <TeamLogo team={visitingTeam} />
            <TeamLink team={visitingTeam} />
          </div>
        )}

        <Text
          variant='paragraphSmall'
          weight='bold'
          color={isPlaying ? colors.PRIMARY : colors.NEUTRAL_0}
        >
          {highlight.title}
        </Text>
        {highlight.body && (
          <Text
            variant='paragraphXSmall'
            color={isPlaying ? colors.NEUTRAL_0 : colors.NEUTRAL_50}
            weight='regular'
            style={css`
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              white-space: normal;
            `}
          >
            {highlight.body}
          </Text>
        )}
        {highlight.created_at && (
          <Text
            variant='paragraphXSmall'
            color={colors.NEUTRAL_50}
            weight='regular'
          >
            {dayjs(highlight.created_at).format('MMM D, YYYY')}
          </Text>
        )}
      </div>
      {canShare && (
        <div onClick={(e) => e.stopPropagation()}>
          <ShareModal
            isOpen={isShareOpen}
            onClose={() => setIsShareOpen(false)}
            entityType='highlight'
            entityName={highlight.title || 'Highlight'}
            shareUrl={shareUrl}
          />
        </div>
      )}
    </div>
  )
}

export default PlayerHighlightCard
